import { Patient } from '@/types';
import { useHospitalProfileStore } from '@/lib/stores/hospitalProfile';

export const printPatientOPSheet = (patient: Patient, doctors: any[] = []) => {
  const { profile } = useHospitalProfileStore.getState();
  
  // Create a new window for printing
  const printWindow = window.open('', '_blank', 'width=800,height=600');
  
  if (!printWindow) {
    console.error('Could not open print window');
    return;
  }

  const currentDate = new Date().toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const currentTime = new Date().toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });

  // Get doctor name
  const getDoctorName = (doctorId: string) => {
    if (!doctorId) return 'Unassigned';
    const doctor = doctors.find(d => d.id === doctorId);
    return doctor ? doctor.name : 'Unassigned';
  };

  // Format patient ID
  const formatPatientId = (id: string) => {
    return id.replace(/(.{4})(.{4})(.{4})(.{4})/, '$1-$2-$3-$4');
  };

  // Calculate services cost
  const servicesCost = patient.services?.reduce((total, service) => total + service.price, 0) || 0;
  const amountPaid = patient.amountPaid || 0;
  const dueAmount = servicesCost - amountPaid;

  const printContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>OP Sheet - ${patient.name}</title>
      <style>
        @media print {
          @page {
            size: A4;
            margin: 0.5in;
          }
        }
        
        body {
          font-family: 'Arial', sans-serif;
          font-size: 12px;
          line-height: 1.4;
          margin: 0;
          padding: 0;
          color: #333;
        }
        
        .header {
          text-align: center;
          border-bottom: 2px solid #2563eb;
          padding-bottom: 15px;
          margin-bottom: 20px;
        }
        
        .hospital-name {
          font-size: 24px;
          font-weight: bold;
          color: #2563eb;
          margin-bottom: 5px;
        }
        
        .hospital-details {
          font-size: 11px;
          color: #666;
          margin-bottom: 5px;
        }
        
        .patient-section {
          margin-bottom: 25px;
        }
        
        .section-title {
          font-size: 14px;
          font-weight: bold;
          color: #2563eb;
          border-bottom: 1px solid #e5e7eb;
          padding-bottom: 5px;
          margin-bottom: 10px;
        }
        
        .patient-info {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 15px;
          margin-bottom: 15px;
        }
        
        .info-item {
          margin-bottom: 8px;
        }
        
        .info-label {
          font-weight: bold;
          color: #374151;
        }
        
        .info-value {
          color: #6b7280;
        }
        
        .services-section {
          margin-bottom: 25px;
        }
        
        .service-item {
          display: flex;
          justify-content: space-between;
          padding: 5px 0;
          border-bottom: 1px dotted #d1d5db;
        }
        
        .prescription-section {
          margin-top: 30px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          padding: 20px;
          min-height: 300px;
        }
        
        .prescription-title {
          font-size: 16px;
          font-weight: bold;
          color: #2563eb;
          text-align: center;
          margin-bottom: 20px;
          border-bottom: 1px solid #e5e7eb;
          padding-bottom: 10px;
        }
        
        .prescription-lines {
          margin-bottom: 15px;
          min-height: 400px;
        }
        
        .doctor-signature {
          display: flex;
          justify-content: space-between;
          margin-top: 20px;
        }
        
        .signature-box {
          width: 200px;
          text-align: center;
        }
        
        .signature-line {
          border-bottom: 1px solid #333;
          height: 20px;
          margin-bottom: 5px;
        }
        

        
        .urgent-badge {
          background-color: #fef3c7;
          color: #92400e;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 10px;
          font-weight: bold;
        }
        
        .status-badge {
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 10px;
          font-weight: bold;
        }
        
        .status-waiting {
          background-color: #fef3c7;
          color: #92400e;
        }
        
        .status-in-consultation {
          background-color: #dbeafe;
          color: #1e40af;
        }
        
        .status-completed {
          background-color: #d1fae5;
          color: #065f46;
        }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="hospital-name">${profile.name}</div>
        <div class="hospital-details">
          ${profile.address}<br>
          Phone: ${profile.phone1} | Email: ${profile.email}<br>
          ${profile.website}
        </div>
      </div>
      
      <div class="patient-section">
        <div class="section-title">Patient Information</div>
        <div class="patient-info">
          <div>
            <div class="info-item">
              <span class="info-label">Name:</span>
              <span class="info-value">${patient.name}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Age:</span>
              <span class="info-value">${patient.age}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Gender:</span>
              <span class="info-value">${patient.gender}</span>
            </div>
          </div>
          <div>
            <div class="info-item">
              <span class="info-label">Patient ID:</span>
              <span class="info-value">${formatPatientId(patient.id)}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Date:</span>
              <span class="info-value">${currentDate}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Doctor:</span>
              <span class="info-value">${getDoctorName(patient.doctorId || '')}</span>
            </div>
          </div>
        </div>
      </div>
      
      <div class="prescription-lines">
        <!-- Empty space for writing -->
      </div>
    </body>
    </html>
  `;

  printWindow.document.write(printContent);
  printWindow.document.close();
  
  // Wait for content to load, then print
  printWindow.onload = () => {
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };
};
